import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActivityIndicator,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import MapView, { Marker, Polyline, PROVIDER_GOOGLE } from "react-native-maps";
import Text from "@components/CustomText";
import { getLiveDbTrail } from "@services/api";
import Header from "../PartnerRoutingAndPacking/components/Header";
import { tokens, sharedStyles } from "../PartnerRoutingAndPacking/styles";
import ZoomControls from "../LiveDbLocations/components/ZoomControls";

const INDIGO = "#4F46E5";
const GREEN = "#16A34A";
const WHITE = "#FFFFFF";
// Same dashed-indigo the delivery-tracker UI uses for offline bridges.
const BRIDGE_INDIGO = "#5C6BC0";

// Raw-pings fallback only: a silence longer than this splits the trail
// into separate drive segments (server-matched segments carry their own
// split points).
const OFFLINE_GAP_MS = 5 * 60 * 1000;

// Small dot marker; collapsable={false} + a brief tracking window are
// required for custom-view markers to draw on Android (see DbMarker.js).
const TrailDot = ({ coordinate, color, size }) => {
  const [track, setTrack] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setTrack(false), 800);
    return () => clearTimeout(t);
  }, []);
  return (
    <Marker
      coordinate={coordinate}
      anchor={{ x: 0.5, y: 0.5 }}
      tracksViewChanges={track}
      tracksInfoWindowChanges={false}
    >
      <View
        collapsable={false}
        style={[
          styles.dot,
          {
            width: size,
            height: size,
            borderRadius: size / 2,
            backgroundColor: color,
          },
        ]}
      />
    </Marker>
  );
};

const LiveDbTrail = ({ navigation, route }) => {
  const db = route?.params?.db || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const mapRef = useRef(null);
  const hasFittedRef = useRef(false);

  const fetchData = useCallback(async () => {
    if (!db.cp_db_id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getLiveDbTrail(db.cp_db_id);
      setData(res?.data || null);
    } catch (_e) {
      setError("Could not fetch the trail");
    } finally {
      setLoading(false);
    }
  }, [db.cp_db_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Continuous drive segments (solid lines). Server sends road-snapped
  // OSRM segments split at offline periods; when matching failed, derive
  // segments from the raw pings by splitting on time gaps.
  const segments = useMemo(() => {
    const toCoord = (p) => ({ latitude: p.lat, longitude: p.lng });
    if (data?.segments?.length) {
      return data.segments.map((seg) => seg.map(toCoord));
    }
    const pings = data?.pings || [];
    const out = [];
    let cur = [];
    let prevTs = null;
    for (const p of pings) {
      const ts = new Date(p.ts).getTime();
      if (prevTs !== null && ts - prevTs > OFFLINE_GAP_MS && cur.length) {
        out.push(cur);
        cur = [];
      }
      cur.push(toCoord(p));
      prevTs = ts;
    }
    if (cur.length) out.push(cur);
    return out;
  }, [data]);

  // Dashed offline bridges. The server sends road-shaped bridge
  // geometries (via OSRM /route) between drive segments; in raw-pings
  // fallback mode, derive straight connectors between the segments.
  const gaps = useMemo(() => {
    if (data?.segments?.length) {
      return (data.bridges || [])
        .map((b) =>
          (b.points || []).map((p) => ({
            latitude: p.lat,
            longitude: p.lng,
          })),
        )
        .filter((g) => g.length >= 2);
    }
    const out = [];
    for (let i = 1; i < segments.length; i += 1) {
      const prev = segments[i - 1];
      out.push([prev[prev.length - 1], segments[i][0]]);
    }
    return out;
  }, [data, segments]);

  const allPoints = useMemo(() => segments.flat(), [segments]);

  const fitOnce = useCallback(() => {
    if (hasFittedRef.current || !mapRef.current || allPoints.length < 2) return;
    hasFittedRef.current = true;
    mapRef.current.fitToCoordinates(allPoints, {
      edgePadding: { top: 60, right: 40, bottom: 60, left: 40 },
      animated: false,
    });
  }, [allPoints]);

  useEffect(() => {
    fitOnce();
  }, [fitOnce]);

  const subtitle = data
    ? data.total_pings
      ? `${data.total_pings} pings · ${data.date}`
      : `no pings on ${data.date}`
    : "";

  return (
    <View style={sharedStyles.screen}>
      <Header
        title={db.name || "Trail"}
        subtitle={subtitle}
        onBack={() => navigation.goBack()}
        right={
          <TouchableOpacity
            onPress={fetchData}
            disabled={loading}
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
            style={styles.refreshBtn}
          >
            {loading ? (
              <ActivityIndicator size="small" color={tokens.textPrimary} />
            ) : (
              <Text style={styles.refreshGlyph}>↻</Text>
            )}
          </TouchableOpacity>
        }
      />

      {error ? (
        <View style={sharedStyles.centered}>
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity style={styles.retryBtn} onPress={fetchData}>
            <Text style={styles.retryText}>Retry</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <View style={styles.mapWrap}>
          <MapView
            ref={mapRef}
            provider={PROVIDER_GOOGLE}
            googleRenderer={"LEGACY"}
            style={StyleSheet.absoluteFill}
            minZoomLevel={2}
            maxZoomLevel={20}
            toolbarEnabled={false}
            showsCompass={false}
            zoomControlEnabled={false}
            onMapReady={fitOnce}
            onLayout={fitOnce}
          >
            {segments.map((seg, i) =>
              seg.length >= 2 ? (
                <Polyline
                  key={`seg-${i}`}
                  coordinates={seg}
                  strokeColor={INDIGO}
                  strokeWidth={3}
                />
              ) : null,
            )}
            {gaps.map((gap, i) => (
              <Polyline
                key={`gap-${i}`}
                coordinates={gap}
                strokeColor={BRIDGE_INDIGO}
                strokeWidth={3}
                lineDashPattern={[10, 6]}
              />
            ))}
            {allPoints.length ? (
              <TrailDot coordinate={allPoints[0]} color={GREEN} size={12} />
            ) : null}
            {allPoints.length >= 2 ? (
              <TrailDot
                coordinate={allPoints[allPoints.length - 1]}
                color={INDIGO}
                size={14}
              />
            ) : null}
          </MapView>
          <ZoomControls mapRef={mapRef} style={styles.zoomControls} />
          {data && !data.total_pings && !loading ? (
            <View style={styles.emptyOverlay}>
              <Text style={styles.emptyText}>No trail for today yet</Text>
            </View>
          ) : null}
        </View>
      )}
    </View>
  );
};

export default LiveDbTrail;

const styles = StyleSheet.create({
  mapWrap: { flex: 1 },
  zoomControls: { bottom: 32 },
  refreshBtn: {
    width: 36,
    height: 36,
    alignItems: "center",
    justifyContent: "center",
  },
  refreshGlyph: {
    color: tokens.textPrimary,
    fontSize: 22,
    fontWeight: "600",
  },
  dot: {
    borderWidth: 2,
    borderColor: WHITE,
    elevation: 2,
  },
  emptyOverlay: {
    position: "absolute",
    top: 16,
    alignSelf: "center",
    backgroundColor: tokens.surface,
    borderRadius: tokens.radius.full,
    paddingHorizontal: tokens.spacing.large,
    paddingVertical: tokens.spacing.small,
    elevation: 2,
  },
  emptyText: { color: tokens.textSecondary },
  errorText: {
    color: tokens.textSecondary,
    marginBottom: tokens.spacing.medium,
  },
  retryBtn: {
    paddingHorizontal: tokens.spacing.xlarge,
    paddingVertical: tokens.spacing.small,
    borderRadius: tokens.radius.full,
    backgroundColor: INDIGO,
  },
  retryText: { color: WHITE, fontWeight: "700" },
});
